import { computed, ref } from 'vue'
import type { SchoolPeriodUser } from '@/core/domain/school/user/SchoolPeriodUser'

/**
 * Composable pour gérer la recherche et le tri dans les tableaux d'utilisateurs
 */
export function useSchoolPeriodUsersTable() {
  // État local
  const searchQuery = ref('')
  const sortField = ref<string>('lastname')
  const sortOrder = ref<'asc' | 'desc'>('asc')

  /**
   * Filtre une liste d'utilisateurs selon la recherche
   */
  function filterUsers(users: SchoolPeriodUser[]): SchoolPeriodUser[] {
    if (!searchQuery.value) return users

    const query = searchQuery.value.toLowerCase()
    return users.filter(user => {
      const fullName = `${user.firstname} ${user.lastname}`.toLowerCase()
      const username = user.username?.toLowerCase() || ''
      const email = user.email?.toLowerCase() || ''

      return (
        fullName.includes(query) ||
        username.includes(query) ||
        email.includes(query)
      )
    })
  }

  /**
   * Trie une liste d'utilisateurs
   */
  function sortUsers(users: SchoolPeriodUser[]): SchoolPeriodUser[] {
    return [...users].sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (sortField.value) {
        case 'lastname':
          aValue = a.lastname.toLowerCase()
          bValue = b.lastname.toLowerCase()
          break
        case 'firstname':
          aValue = a.firstname.toLowerCase()
          bValue = b.firstname.toLowerCase()
          break
        case 'username':
          aValue = (a.username || '').toLowerCase()
          bValue = (b.username || '').toLowerCase()
          break
        case 'email':
          aValue = (a.email || '').toLowerCase()
          bValue = (b.email || '').toLowerCase()
          break
        case 'createdAt':
          aValue = a.createdAt || ''
          bValue = b.createdAt || ''
          break
        default:
          aValue = a.lastname.toLowerCase()
          bValue = b.lastname.toLowerCase()
      }

      const compareResult = aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      return sortOrder.value === 'asc' ? compareResult : -compareResult
    })
  }

  /**
   * Filtre et trie une liste d'utilisateurs
   */
  function processUsers(users: SchoolPeriodUser[]): SchoolPeriodUser[] {
    const filtered = filterUsers(users)
    return sortUsers(filtered)
  }

  /**
   * Gère le changement de tri
   */
  function onSort(field: string) {
    if (sortField.value === field) {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
    } else {
      sortField.value = field
      sortOrder.value = 'asc'
    }
  }

  /**
   * Réinitialise la recherche
   */
  function resetSearch() {
    searchQuery.value = ''
  }

  return {
    searchQuery,
    sortField,
    sortOrder,
    filterUsers,
    sortUsers,
    processUsers,
    onSort,
    resetSearch
  }
}